<template>
  <div class="emptyStates">
    <div v-if="compact && hasLandlordNoTenancy" class="emptyStates__compact">
      <hl-Button :disabled="rentalObjectId === undefined" @click="goToContractApp">
        {{ translate('emptyStates.noTenancy.buttons.button1.text') }}
      </hl-Button>
      <hl-Button
        outline
        variant="extra"
        :disabled="rentalObjectId === undefined"
        @click="goToCreateExistingTenancyView"
      >
        {{ translate('emptyStates.noTenancy.buttons.button2.text') }}
      </hl-Button>
    </div>

    <template v-else>
      <BlockContainer v-if="hasLandlordNoRentalObject" class="emptyStates__createRentalObject mt-5">
        <h4>
          {{ translate('emptyStates.noRentalObject.header.title') }}
        </h4>
        <hl-Button class="mt-3" @click="goToCreateRentalObjectView">
          <div class="emptyStates__createRentalObject__buttonContent">
            <i class="budicon-plus-ui"></i>
            {{ translate('emptyStates.noRentalObject.button.text') }}
          </div>
        </hl-Button>
      </BlockContainer>

      <BlockContainer v-else-if="hasLandlordNoTenancy" class="emptyStates__createTenancy mt-5">
        <h4>
          {{ translate('emptyStates.noTenancy.header.title') }}
        </h4>

        <div class="mt-4">
          <hl-Select
            v-if="!rentalObjectIdProp"
            :label="translate('emptyStates.noTenancy.inputs.rentalObject.label')"
            :options="rentalObjectOptions"
            @input="rentalObjectId = $event"
          />

          <div class="emptyStates__createTenancy__buttons mt-2">
            <hl-Button :disabled="rentalObjectId === undefined" @click="goToContractApp">
              {{ translate('emptyStates.noTenancy.buttons.button1.text') }}
            </hl-Button>
            <hl-Button
              outline
              variant="extra"
              :disabled="rentalObjectId === undefined"
              @click="goToCreateExistingTenancyView"
            >
              {{ translate('emptyStates.noTenancy.buttons.button2.text') }}
            </hl-Button>
          </div>
        </div>
      </BlockContainer>
    </template>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import BlockContainer from '@/components/BlockContainer';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import Select from '@modules/husleie-framework/src/components/forms/inputs/select/Select.vue';
import i18nCommon from '@modules/i18n/mixins/Common';
import RedirectOperations from '@modules/husleie-framework/src/static-classes/RedirectOperations.class';
import { AppRedirect } from '@modules/husleie-framework/src/enums/AppRedirect.enum';

export default Vue.extend({
  name: 'EmptyStates',
  mixins: [i18nCommon],
  data() {
    return {
      rentalObjectId: undefined,
    };
  },
  props: {
    compact: {
      type: Boolean,
      required: false,
    },
    hasLandlordNoRentalObject: {
      type: Boolean,
      required: true,
    },
    hasLandlordNoTenancy: {
      type: Boolean,
      required: true,
    },
    rentalObjectIdProp: {
      type: Number,
      required: false,
    },
  },
  components: {
    BlockContainer,
    'hl-Button': Button,
    'hl-Select': Select,
  },
  watch: {
    rentalObjectIdProp: {
      handler(value) {
        if (value) {
          this.rentalObjectId = value;
        } else {
          this.fetchCondensedRentalObjectList(this.getLandlordId);
        }
      },
      immediate: true,
    },
  },
  computed: {
    ...mapGetters(['getLandlordId', 'getCondensedRentalObjects']),
    rentalObjectOptions() {
      return [
        {
          name: this.translate('emptyStates.noTenancy.rentalObject.name'),
          value: '',
        },
        ...this.getCondensedRentalObjects
          .map(item => ({
            name: item.name,
            value: item.id,
          }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      ];
    },
  },
  methods: {
    ...mapActions(['fetchCondensedRentalObjectList']),

    goToCreateRentalObjectView() {
      this.$router.push({
        name: 'newRO',
        params: {
          landlordId: this.getLandlordId,
        },
      });
    },
    goToContractApp() {
      return RedirectOperations.goToURL(
        AppRedirect.CONTRACT_CREATE,
        {
          rentalObjectId: this.rentalObjectId,
        },
        true
      );
    },
    goToCreateExistingTenancyView() {
      this.$router.push({
        name: 'rentalObject_new_tenancy',
        params: {
          landlordId: this.getLandlordId,
          rentalObjectId: this.rentalObjectId,
        },
      });
    },
  },
});
</script>

<style lang="scss" scoped>
.emptyStates__compact {
  display: flex;
  gap: 0rem 1rem;
}
.emptyStates__createRentalObject {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem;
}
.emptyStates__createRentalObject button {
  padding: 0.4rem 0.7rem;
}
.emptyStates__createRentalObject__buttonContent {
  display: flex;
  align-items: center;
  gap: 0rem 0.3rem;
}
.emptyStates__createRentalObject__buttonContent i {
  font-size: 1.6rem;
}
.emptyStates__createTenancy {
  padding: 3rem;
}
.emptyStates__createTenancy__buttons {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem 0rem;
}

@media (min-width: 885px) {
  .emptyStates__createTenancy__buttons {
    display: flex;
    gap: 0rem 1rem;
  }
}
</style>
